import type { Risk } from '../../types/api';
// Vite resolves this so the CSV is emitted and reachable in dev + production builds
import worldCitiesCsvUrl from '../../data/world_cities.csv?url';

/** One row from `world_cities.csv` used for geocoding. */
export interface WorldCityRecord {
  city: string;
  cityAscii: string;
  lat: number;
  lng: number;
  country: string;
  /** From CSV when present (used for coverage map → GeoJSON ISO3 `id`). */
  iso2?: string;
  iso3?: string;
}

/** Result of matching a risk’s location/country to the CSV. */
export interface RiskLocationMatch {
  risk: Risk;
  lat: number;
  lng: number;
  matchedCity: string;
  matchedCountry: string;
  matchScore: number;
}

/** Minimum similarity (0–1) to accept a city match when not exact. */
const MIN_CITY_SCORE = 0.72;

/** Max Levenshtein distance still considered (scaled by length) for very short names. */
const MAX_RELATIVE_DISTANCE = 0.35;

let cachedRecords: WorldCityRecord[] | null = null;
let cachedByCountry: Map<string, WorldCityRecord[]> | null = null;

/** Built from CSV when `loadWorldCityRecords` runs; keyed like `csvCountryToKey`. */
let countryKeyToIsoCache: Map<string, { iso2: string; iso3: string }> | null = null;
let iso2ToIso3Cache: Map<string, string> | null = null;
/** ISO code (upper, no spaces) → CSV country key (e.g. `AU` / `AUS` → `australia`). */
let iso2ToCountryKeyCache: Map<string, string> | null = null;
let iso3ToCountryKeyCache: Map<string, string> | null = null;

export interface WorldCitiesIsoIndexes {
  countryKeyToIso: Map<string, { iso2: string; iso3: string }>;
  iso2ToIso3: Map<string, string>;
}

/** Same key used to bucket CSV rows by country (for ISO lookup on unit `country` strings). */
export function countryKeyFromWorldCitiesCountry(country: string | undefined | null): string {
  return csvCountryToKey(country || '');
}

function rebuildWorldCitiesIsoIndexes(records: WorldCityRecord[]): void {
  const countryKeyToIso = new Map<string, { iso2: string; iso3: string }>();
  const iso2ToIso3 = new Map<string, string>();
  const iso2ToCountryKey = new Map<string, string>();
  const iso3ToCountryKey = new Map<string, string>();
  for (const row of records) {
    if (!row.country || !row.iso2 || !row.iso3) continue;
    const k = csvCountryToKey(row.country);
    if (!k) continue;
    const i2 = row.iso2.trim().toUpperCase();
    const i3 = row.iso3.trim().toUpperCase();
    if (i2.length !== 2 || i3.length !== 3) continue;
    if (!countryKeyToIso.has(k)) countryKeyToIso.set(k, { iso2: i2, iso3: i3 });
    iso2ToIso3.set(i2, i3);
    if (!iso2ToCountryKey.has(i2)) iso2ToCountryKey.set(i2, k);
    if (!iso3ToCountryKey.has(i3)) iso3ToCountryKey.set(i3, k);
  }
  countryKeyToIsoCache = countryKeyToIso;
  iso2ToIso3Cache = iso2ToIso3;
  iso2ToCountryKeyCache = iso2ToCountryKey;
  iso3ToCountryKeyCache = iso3ToCountryKey;
}

/** Populated after `loadWorldCityRecords()` resolves. */
export function getWorldCitiesIsoIndexes(): WorldCitiesIsoIndexes | null {
  if (!countryKeyToIsoCache || !iso2ToIso3Cache) return null;
  return { countryKeyToIso: countryKeyToIsoCache, iso2ToIso3: iso2ToIso3Cache };
}

/**
 * Maps many API / common country spellings to a single normalized key
 * so CSV rows can be grouped with risk.country.
 */
const COUNTRY_ALIAS_TO_CANONICAL: Record<string, string> = {
  usa: 'united states',
  'united states of america': 'united states',
  'u.s.': 'united states',
  'u.s.a.': 'united states',
  us: 'united states',
  america: 'united states',
  uk: 'united kingdom',
  'great britain': 'united kingdom',
  britain: 'united kingdom',
  england: 'united kingdom',
  scotland: 'united kingdom',
  wales: 'united kingdom',
  'northern ireland': 'united kingdom',
  uae: 'united arab emirates',
  'u.a.e.': 'united arab emirates',
  russia: 'russian federation',
  'south korea': 'korea south',
  'republic of korea': 'korea south',
  korea: 'korea south',
  'korea, south': 'korea south',
  'north korea': 'korea north',
  'korea, north': 'korea north',
  'dr congo': 'congo kinshasa',
  drc: 'congo kinshasa',
  'congo (kinshasa)': 'congo kinshasa',
  'democratic republic of the congo': 'congo kinshasa',
  'congo (brazzaville)': 'congo brazzaville',
  'republic of the congo': 'congo brazzaville',
  ivory: "cote d'ivoire",
  'côte divoire': "cote d'ivoire",
  bolivia: 'bolivia, plurinational state of',
  venezuela: 'venezuela, bolivarian republic of',
  iran: 'iran, islamic republic of',
  syria: 'syrian arab republic',
  vietnam: 'viet nam',
  laos: "lao people's democratic republic",
  moldova: 'moldova, republic of',
  tanzania: 'tanzania, united republic of',
  macedonia: 'north macedonia',
  swaziland: 'eswatini',
  czechia: 'czech republic',
  holland: 'netherlands',
  burma: 'myanmar',
};

/**
 * Strip diacritics and normalize whitespace / punctuation for comparison.
 */
export function normalizeGeoString(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') return '';
  return String(value)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[.']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function canonicalCountryKey(normalizedCountry: string): string {
  if (!normalizedCountry) return '';
  const alias = COUNTRY_ALIAS_TO_CANONICAL[normalizedCountry];
  if (alias) return alias;
  return normalizedCountry;
}

/**
 * Normalize CSV country names to the same key space as risk.country.
 */
function csvCountryToKey(csvCountry: string): string {
  const n = normalizeGeoString(csvCountry);
  return canonicalCountryKey(n);
}

/**
 * Parse one CSV line respecting quoted fields (commas inside quotes).
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (c === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim().replace(/^"|"$/g, ''));
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const row = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) row[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = row[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
      prev = tmp;
    }
  }
  return row[n];
}

/**
 * Similarity in [0, 1]: 1 = identical after normalize.
 */
function stringSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshtein(a, b);
  if (dist / maxLen > MAX_RELATIVE_DISTANCE && a.length > 4 && b.length > 4) {
    /* allow substring boosts below */
  }
  return 1 - dist / maxLen;
}

/**
 * Best similarity between risk city string and CSV city (ascii + display name).
 */
function cityMatchScore(riskCityNorm: string, row: WorldCityRecord): number {
  const ascii = normalizeGeoString(row.cityAscii);
  const city = normalizeGeoString(row.city);
  if (!riskCityNorm) return 0;

  let best = 0;
  for (const candidate of [ascii, city]) {
    if (!candidate) continue;
    if (riskCityNorm === candidate) return 1;
    if (riskCityNorm.includes(candidate) || candidate.includes(riskCityNorm)) {
      const shorter = Math.min(riskCityNorm.length, candidate.length);
      const longer = Math.max(riskCityNorm.length, candidate.length);
      best = Math.max(best, 0.85 + 0.1 * (shorter / longer));
    }
    best = Math.max(best, stringSimilarity(riskCityNorm, candidate));
    /* Token overlap: "ho chi minh" vs "ho chi minh city" */
    const ta = new Set(riskCityNorm.split(' ').filter(Boolean));
    const tb = new Set(candidate.split(' ').filter(Boolean));
    if (ta.size && tb.size) {
      let inter = 0;
      for (const t of ta) if (tb.has(t)) inter++;
      const union = ta.size + tb.size - inter;
      const jaccard = union ? inter / union : 0;
      if (jaccard >= 0.5) best = Math.max(best, 0.7 + 0.25 * jaccard);
    }
  }
  return Math.min(1, best);
}

/**
 * Take primary city token from risk.location (e.g. "Paris" or "Paris, Île-de-France").
 */
export function extractRiskCityName(location: string | undefined | null): string {
  if (location == null || !String(location).trim()) return '';
  const first = String(location).split(',')[0].trim();
  return normalizeGeoString(first);
}

/**
 * Parse full CSV text into records (skips bad rows).
 */
export function parseWorldCitiesCsv(csvText: string): WorldCityRecord[] {
  const lines = csvText.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length < 2) return [];
  const records: WorldCityRecord[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    if (cols.length < 5) continue;
    const [city, cityAscii, latStr, lngStr, country, iso2Raw, iso3Raw] = cols;
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (Number.isNaN(lat) || Number.isNaN(lng) || !country) continue;
    const iso2 = cols.length >= 7 && iso2Raw ? iso2Raw.trim().toUpperCase() : undefined;
    const iso3 = cols.length >= 7 && iso3Raw ? iso3Raw.trim().toUpperCase() : undefined;
    records.push({
      city: city || cityAscii,
      cityAscii: cityAscii || city,
      lat,
      lng,
      country,
      ...(iso2 && iso3 ? { iso2, iso3 } : {}),
    });
  }
  return records;
}

function buildCountryIndex(records: WorldCityRecord[]): Map<string, WorldCityRecord[]> {
  const map = new Map<string, WorldCityRecord[]>();
  for (const row of records) {
    const key = csvCountryToKey(row.country);
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  }
  return map;
}

/**
 * Load and cache world cities from `src/data/world_cities.csv`.
 */
export async function loadWorldCityRecords(): Promise<WorldCityRecord[]> {
  if (cachedRecords) return cachedRecords;
  const res = await fetch(worldCitiesCsvUrl);
  if (!res.ok) throw new Error(`world_cities.csv fetch failed: ${res.status}`);
  const text = await res.text();
  cachedRecords = parseWorldCitiesCsv(text);
  cachedByCountry = buildCountryIndex(cachedRecords);
  rebuildWorldCitiesIsoIndexes(cachedRecords);
  return cachedRecords;
}

/**
 * Clear caches (e.g. tests).
 */
export function clearWorldCityCache(): void {
  cachedRecords = null;
  cachedByCountry = null;
  countryKeyToIsoCache = null;
  iso2ToIso3Cache = null;
  iso2ToCountryKeyCache = null;
  iso3ToCountryKeyCache = null;
}

/**
 * Take the trailing comma token of a location as a possible country
 * (e.g. "Sydney, Australia" → "Australia"). Empty when there is no comma.
 */
export function extractRiskCountryHint(location: string | undefined | null): string {
  const parts = String(location ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length >= 2 ? parts[parts.length - 1] : '';
}

/**
 * Resolve any country string (full name, alias, or ISO2/ISO3 code) to a CSV
 * country key that actually has rows. Returns '' when it cannot be resolved
 * to a known bucket.
 */
function resolveKnownCountryKey(
  rawCountry: string | undefined | null,
  byCountry: Map<string, WorldCityRecord[]>
): string {
  const norm = normalizeGeoString(rawCountry);
  if (!norm) return '';

  const canonical = canonicalCountryKey(norm);
  if (byCountry.has(canonical)) return canonical;

  /* ISO 3166 code (e.g. `AU`, `AUS`) → CSV country key. */
  const compact = norm.replace(/[^a-z]/g, '').toUpperCase();
  if (compact.length === 2 && iso2ToCountryKeyCache?.has(compact)) {
    return iso2ToCountryKeyCache.get(compact)!;
  }
  if (compact.length === 3 && iso3ToCountryKeyCache?.has(compact)) {
    return iso3ToCountryKeyCache.get(compact)!;
  }
  return '';
}

/**
 * Country for a risk, resolved to a CSV bucket key. Prefers `risk.country`
 * (name or ISO code), then a country embedded in the `location` tail.
 */
export function resolveRiskCountryKey(
  risk: Pick<Risk, 'country' | 'location'>,
  byCountry: Map<string, WorldCityRecord[]>
): string {
  return (
    resolveKnownCountryKey(risk.country, byCountry) ||
    resolveKnownCountryKey(extractRiskCountryHint(risk.location), byCountry)
  );
}

/**
 * Display country name (as spelled in the CSV, e.g. "Australia") for a risk,
 * so the geocode fallback can send a real country instead of an ISO code.
 * Falls back to the raw `risk.country` string when unknown.
 */
export function resolveRiskCountryName(risk: Pick<Risk, 'country' | 'location'>): string {
  if (!cachedByCountry) return (risk.country || '').trim();
  const key = resolveRiskCountryKey(risk, cachedByCountry);
  const rows = key ? cachedByCountry.get(key) : undefined;
  return rows && rows.length > 0 ? rows[0].country : (risk.country || '').trim();
}

/**
 * Find the best CSV row for one risk (location + country).
 */
export function findBestWorldCityForRisk(
  risk: Risk,
  byCountry: Map<string, WorldCityRecord[]>
): RiskLocationMatch | null {
  /* Country-first: resolve full name, alias, or ISO2/ISO3 code to a CSV bucket. */
  const countryNorm =
    resolveRiskCountryKey(risk, byCountry) ||
    canonicalCountryKey(normalizeGeoString(risk.country));
  const cityNorm = extractRiskCityName(risk.location);
  if (!countryNorm || !cityNorm) return null;

  let candidates = byCountry.get(countryNorm) || [];

  /* Fallback: scan countries whose key starts with same token (weak) */
  /* Weak country fallback: only keys that substantially overlap (avoids huge scans). */
  if (candidates.length === 0 && countryNorm.length >= 4) {
    for (const [key, rows] of byCountry) {
      if (key.length < 4) continue;
      if (key.includes(countryNorm) || countryNorm.includes(key)) {
        candidates = candidates.concat(rows);
        if (candidates.length > 5000) break;
      }
    }
  }

  if (candidates.length === 0) return null;

  let best: { row: WorldCityRecord; score: number } | null = null;
  for (const row of candidates) {
    const score = cityMatchScore(cityNorm, row);
    if (!best || score > best.score) best = { row, score };
  }

  if (!best || best.score < MIN_CITY_SCORE) return null;

  return {
    risk,
    lat: best.row.lat,
    lng: best.row.lng,
    matchedCity: best.row.cityAscii || best.row.city,
    matchedCountry: best.row.country,
    matchScore: best.score,
  };
}

/**
 * Match many risks to coordinates. Each risk uses its own best city row
 * (same city in multiple risks → duplicate pins; caller can merge).
 */
export async function matchRisksToWorldCities(risks: Risk[]): Promise<RiskLocationMatch[]> {
  await loadWorldCityRecords();
  const byCountry = cachedByCountry!;
  const out: RiskLocationMatch[] = [];
  for (const risk of risks) {
    const m = findBestWorldCityForRisk(risk, byCountry);
    if (m) out.push(m);
  }
  return out;
}
